/**
 * 1.3.81: YUIChat 项目 - 企业用户注册页面
 * 非SSO企业用户注册流程：
 * 1. 填写企业信息
 * 2. 填写邮箱密码
 * 3. 发送验证邮件
 * 4. 验证后跳转到管理页面
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Loader2, CheckCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { signUpWithEmail } from '../services/authService';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { translateAuthError } from '../utils/errorTranslation';
import { validatePasswordStrength, PASSWORD_MIN_LENGTH } from '../utils/passwordValidation';

export function BusinessRegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    companyName: '',
    companySize: '',
    industry: '',
    jobTitle: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showVerifyScreen, setShowVerifyScreen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordChecks = useMemo(() => validatePasswordStrength(formData.password).checks, [formData.password]);

  // 企业规模选项
  const COMPANY_SIZES = [
    { value: '2-5', label: t('businessSetup.size1to5') },
    { value: '6-20', label: t('businessSetup.size6to20') },
    { value: '21-50', label: t('businessSetup.size21to50') },
    { value: '51+', label: t('businessSetup.size51plus') },
  ];

  // 行业选项
  const INDUSTRIES = [
    { value: 'technology', label: t('businessSetup.industryTech') },
    { value: 'finance', label: t('businessSetup.industryFinance') },
    { value: 'healthcare', label: t('businessSetup.industryHealthcare') },
    { value: 'education', label: t('businessSetup.industryEducation') },
    { value: 'manufacturing', label: t('businessSetup.industryManufacturing') },
    { value: 'retail', label: t('businessSetup.industryRetail') },
    { value: 'consulting', label: t('businessSetup.industryConsulting') },
    { value: 'media', label: t('businessSetup.industryMedia') },
    { value: 'government', label: t('businessSetup.industryGovernment') },
    { value: 'other', label: t('businessSetup.industryOther') },
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 验证必填字段
    if (!formData.companyName.trim()) {
      setError(t('businessSetup.companyNameRequired'));
      return;
    }
    if (!formData.companySize) {
      setError(t('businessSetup.companySizeRequired'));
      return;
    }
    if (!formData.email.trim()) {
      setError(t('register.emailRequired'));
      return;
    }
    if (!formData.password) {
      setError(t('register.passwordRequired'));
      return;
    }
    const pwValidation = validatePasswordStrength(formData.password);
    if (!pwValidation.isValid) {
      setError(t('passwordRequirement'));
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(t('passwordMismatch'));
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // 注册用户，将企业信息保存到 user_metadata
      const result = await signUpWithEmail(formData.email, formData.password);
      
      if (result.error) {
        setError(translateAuthError(result.error.message, t));
        return;
      }
      
      // 注册成功，将企业信息保存到 localStorage，等邮箱验证后完成企业创建
      localStorage.setItem('pendingBusinessSetup', JSON.stringify({
        companyName: formData.companyName.trim(),
        companySize: formData.companySize,
        industry: formData.industry || undefined,
        jobTitle: formData.jobTitle || undefined,
      }));
      
      // 显示验证邮件提示
      setShowVerifyScreen(true);
    } catch (err: any) {
      setError(err.message || t('businessSetup.createFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  // 返回到注册选择页面
  const handleBack = () => {
    navigate('/register');
  };

  // 返回到登录页面
  const handleBackToLogin = () => {
    navigate('/auth');
  };

  // 验证邮件提示页面
  if (showVerifyScreen) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center animate-in fade-in duration-300">
          <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('verifyYourEmail')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('verificationEmailSent')} <strong>{formData.email}</strong>
          </p>
          <p className="text-sm text-gray-500 mb-6">
            {t('checkYourInbox')}
          </p>
          <div className="bg-purple-50 rounded-xl p-4 mb-6 text-left">
            <h3 className="font-medium text-purple-900 flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 text-purple-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
              </svg>
              {t('register.businessInfoSaved')}
            </h3>
            <p className="text-purple-700 text-sm">
              {t('register.businessInfoSavedDesc', { companyName: formData.companyName })}
            </p>
          </div>
          <button
            onClick={handleBackToLogin}
            className="w-full py-3 px-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300"
          >
            {t('backToLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center p-4">
      {/* 返回按钮 - 左上角 */}
      <button
        onClick={handleBack}
        className="absolute top-4 left-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all duration-300"
        title={t('businessSetup.back')}
      >
        <ArrowLeft className="w-6 h-6" />
      </button>

      {/* 语言切换按钮 - 右上角 */}
      <div className="absolute top-4 right-4">
        <LanguageSwitcher direction="down" />
      </div>
      
      <div className="w-full max-w-lg">

        {/* 表单卡片 */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            {/* 简约扁平风格图标 */}
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center">
              <svg className="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{t('register.businessRegisterTitle')}</h1>
            <p className="text-gray-500 mt-2">{t('register.businessRegisterSubtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* 企业信息分组 */}
            <div className="pb-4 border-b border-gray-100">
              <h3 className="text-sm font-medium text-gray-700 mb-4">{t('register.businessInfoSection')}</h3>
              
              {/* 企业名称 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('businessSetup.companyName')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  placeholder={t('businessSetup.companyNamePlaceholder')}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                />
              </div>

              {/* 企业规模 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('businessSetup.companySize')} <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {COMPANY_SIZES.map(size => (
                    <label
                      key={size.value}
                      className={`
                        flex items-center justify-center px-4 py-3 border rounded-xl cursor-pointer transition-all
                        ${formData.companySize === size.value 
                          ? 'border-purple-500 bg-purple-50 text-purple-700' 
                          : 'border-gray-200 hover:border-purple-300'}
                      `}
                    >
                      <input
                        type="radio"
                        name="companySize"
                        value={size.value}
                        checked={formData.companySize === size.value}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <span className="font-medium">{size.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* 行业（选填） */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('businessSetup.industry')} <span className="text-gray-400">（{t('businessSetup.optional')}）</span>
                </label>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all bg-white"
                >
                  <option value="">{t('businessSetup.industryPlaceholder')}</option>
                  {INDUSTRIES.map(industry => (
                    <option key={industry.value} value={industry.value}>
                      {industry.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* 职位（选填） */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('businessSetup.jobTitle')} <span className="text-gray-400">（{t('businessSetup.optional')}）</span>
                </label>
                <input
                  type="text"
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={handleInputChange}
                  placeholder={t('businessSetup.jobTitlePlaceholder')}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                />
              </div>
            </div>

            {/* 账号信息分组 */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-4">{t('register.accountInfoSection')}</h3>

              {/* 邮箱 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('email')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              {/* 密码 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('password')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                    className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="mt-1.5 space-y-0.5">
                  {[
                    { key: 'length', label: t('passwordRules.length', '至少8个字符') },
                    { key: 'letter', label: t('passwordRules.letter', '包含字母') },
                    { key: 'digit', label: t('passwordRules.digit', '包含数字') },
                    { key: 'special', label: t('passwordRules.special', '包含特殊符号（如 !@#$%）') },
                  ].map(rule => (
                    <p key={rule.key} className={`text-xs flex items-center gap-1 ${
                      passwordChecks[rule.key as keyof typeof passwordChecks]
                        ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      <span>{passwordChecks[rule.key as keyof typeof passwordChecks] ? '✓' : '○'}</span>
                      {rule.label}
                    </p>
                  ))}
                </div>
              </div>

              {/* 确认密码 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('confirmPassword')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                    className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>

            {/* 错误提示 */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* 提交按钮 */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:hover:scale-100 shadow-lg hover:shadow-xl flex items-center justify-center"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  {t('register.registering')}
                </span>
              ) : t('register.registerAndVerify')}
            </button>
          </form>
        </div>

        {/* 底部提示 */}
        <p className="text-center text-gray-500 text-sm mt-6">
          {t('businessSetup.adminNote')}
        </p>
      </div>
    </div>
  );
}
